import React from 'react'
import styled from 'styled-components'

import CardWithLabeledRowsOfButtons from 'pages/Dashboard/cards/shared/CardWithLabeledRowsOfButtons'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import GradientIcon from '@mui/icons-material/Gradient'
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined'
import LinkOffIcon from '@mui/icons-material/LinkOff'

import { color } from 'styles/colors'

import { useSessionContext } from 'hooks/SessionContext'

type Props = {
  healthScores: {
    critical: number
    fair: number
    great: number
  }
  filterHealthScores: {
    needsReplacement: number
    fair: number
  }
  isLoaded: boolean
}

const HehmHealthCard = ({
  healthScores,
  filterHealthScores,
  isLoaded
}: Props) => {
  const { session } = useSessionContext()
  const activeHehmAccount = session?.hehmPlan?.hehm_active === 'hehm_active'
  const healthIcon = activeHehmAccount ? '/img/pmk-health.svg' : '/img/pmk-health-inactive.svg'
  const hasScores =
    Object.values(healthScores).some((score) => score > 0) ||
    Object.values(filterHealthScores).some((score) => score > 0)

  return (
    <CardWithLabeledRowsOfButtons
      icon={<img src={healthIcon} alt="HEHM Health Icon" style={{ marginRight: '15px' }} />}
      activeHehmAccount={activeHehmAccount}
      title={HehmHealthCard.cardTitle}
      applyAccentColorToQuantity
      rows={activeHehmAccount && hasScores ? [
        {
          label: 'Health',
          buttons: [
            {
              text: 'Needs Attention',
              img: '/img/health_icon_red.svg',
              imgAlt: 'Needs attention health status icon',
              quantity: healthScores.critical || 0,
              accentColor: color('healthCritical'),
              apptentiveEventName: 'view_needs_attention_health_customers',
              gaEventName: 'EHMKT Health Red',
              filter: 'hehm_critical',
              tooltip: 'Total number of HVAC Performance Monitoring kits that need immediate attention.'
            },
            {
              text: 'Needs Maintenance',
              img: '/img/health_icon_yellow.svg',
              imgAlt: 'Needs maintenance health status icon',
              quantity: healthScores.fair || 0,
              accentColor: color('healthFair'),
              apptentiveEventName: 'view_fair_health_customers',
              gaEventName: 'EHMKT Health Yellow',
              filter: 'hehm_fair',
              tooltip: 'Total number of HVAC Performance Monitoring Kits that need maintenance.'
            },
            {
              text: 'Good',
              img: '/img/health_icon_green.svg',
              imgAlt: 'Good health status icon',
              quantity: healthScores.great || 0,
              accentColor: color('healthGreat'),
              apptentiveEventName: 'view_good_health_customers',
              gaEventName: 'EHMKT Health Green',
              filter: 'hehm_good',
              tooltip: 'Total number of HVAC Performance Monitoring kits that have a good system health.'
            }
          ]
        },
        {
          label: 'Filter Health',
          buttons: [
            {
              text: 'Needs Replacement',
              icon: <S.FilterHealthIcon />,
              quantity: filterHealthScores.needsReplacement || 0,
              accentColor: color('healthCritical'),
              apptentiveEventName: 'view_needs_replacement_filter_health_customers',
              gaEventName: 'EHMKT Filter Red',
              filter: 'hehm_filter_health_needs_replacement',
              tooltip: 'Total number of HVAC Performance Monitoring kits identified with filters that need to be replaced.'
            },
            {
              text: 'Fair',
              icon: <S.FilterHealthIcon />,
              quantity: filterHealthScores.fair || 0,
              accentColor: color('healthFair'),
              apptentiveEventName: 'view_fair_filter_health_customers',
              gaEventName: 'EHMKT Filter Yellow',
              filter: 'hehm_filter_health_fair',
              tooltip: 'Total number of HVAC Performance Monitoring kits identified with "fair" filter health.'
            }
          ]
        }
      ] : [
        {
          label: activeHehmAccount ? 'No EHMKTs Installed' : 'Ask your dealer admin to activate your account now to start installing HVAC Performance Monitoring Kits.',
          buttons: []
        }
      ]}
      isLoaded={isLoaded}
      hehmEnabled
    />
  )
}

HehmHealthCard.cardTitle = 'HVAC Performance Monitoring Kit Health'
HehmHealthCard.cardKey = 'pmk_health'

export default HehmHealthCard

const S = {
  CriticalHealthIcon: styled(InfoOutlineIcon)`
    color: ${color('healthCritical')};
    font-size: 20px;
  `,
  FairHealthIcon: styled(InfoOutlineIcon)`
    color: ${color('healthFair')};
    font-size: 20px;
  `,
  FilterHealthIcon: styled(GradientIcon)`
    font-size: 20px;
  `,
  GreatHealthIcon: styled(CheckCircleOutlineIcon)`
    color: ${color('healthGreat')};
    font-size: 20px;
  `,
  Disconnected24HoursIcon: styled(LinkOffIcon)`
    color: ${color('disconnected24Hours')};
    font-size: 20px;
  `,
  Disconnected7DaysIcon: styled(LinkOffIcon)`
    color: ${color('disconnected7Days')};
    font-size: 20px;
  `
}
